import type { Role } from '../auth/roles';
import { B2B_ROLES } from '../auth/roles';

/**
 * Customer-portal roles. Today the only customer-type *login* roles are the
 * B2B_* set (B2C/WALK_IN are customer record types, served at the counter).
 * If a dedicated B2C login role is added later, include it here and the whole
 * portal shell follows automatically.
 */
export function isCustomerRole(role: Role | undefined | null): boolean {
  return !!role && B2B_ROLES.includes(role);
}

/** French money formatting matching the mockup: "82,79 EUR". */
export function eur(n: number): string {
  return n.toFixed(2).replace('.', ',') + ' EUR';
}

export function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Abbreviate an over-long product reference/SKU with a middle ellipsis so it stays
 * on one line and can't blow out the catalogue grid. Some TecDoc-derived codes are
 * 60+ unbreakable digits; we keep the meaningful head and tail (e.g.
 * "39700000…1992320"). Short codes are returned untouched. Pair with `title={full}`
 * so the complete value is still available on hover.
 */
export function abbreviateRef(ref: string, head = 10, tail = 6): string {
  if (ref.length <= head + tail + 1) return ref;
  return `${ref.slice(0, head)}…${ref.slice(-tail)}`;
}

/**
 * Brand → accent colour.
 *
 * Curated colours for the manufacturers we can name; a DERIVED one for everyone else. The curated
 * list will never be the whole answer — TecDoc ships 1,226 brands and a search can surface any of
 * them — and leaving the rest on one shared grey made the brand filter unreadable: every line the
 * same colour is the same as no colour at all. So an unknown brand gets a stable hue of its own,
 * hashed from its name, which is what actually makes the list scannable.
 *
 * These are IDENTITY approximations, not certified brand guidelines: close enough that BOSCH reads
 * red and VALEO green, not values to hand a printer.
 */
const BRAND_COLORS: Record<string, string> = {
  // — the mockup's original hand-picked palette —
  VALEO: '#7ab51d',
  DENSO: '#c8102e',
  KOYORAD: '#0a5fb0',
  'AKS DASIS': '#c2161b',
  'AVA COOLING': '#1b8ed6',
  HELLA: '#1a3a6b',
  'MAGNETI MARELLI': '#e2b007',
  BOSCH: '#d8232a',
  SACHS: '#0a5fb0',
  TRW: '#c8102e',
  // — majors that actually turn up in our result sets —
  RIDEX: '#e2231a',
  BREMBO: '#c8102e',
  'MANN-FILTER': '#ffcc00',
  MAHLE: '#009640',
  KNECHT: '#009640',
  FEBI: '#e30613',
  'FEBI BILSTEIN': '#e30613',
  BILSTEIN: '#f7c600',
  SKF: '#0f4c8c',
  INA: '#009ee0',
  LUK: '#005ca9',
  'SCHAEFFLER LUK': '#005ca9',
  FAG: '#00953a',
  CONTINENTAL: '#ffa500',
  CONTITECH: '#ffa500',
  GATES: '#0057b8',
  DAYCO: '#e4002b',
  NGK: '#e2001a',
  CHAMPION: '#d2001b',
  ELRING: '#004b93',
  VICTOR_REINZ: '#c8102e',
  DELPHI: '#e4002b',
  LEMFORDER: '#004a8f',
  ZF: '#005a9c',
  MEYLE: '#e30613',
  OPTIMAL: '#0069b4',
  SWAG: '#e30613',
  'BLUE PRINT': '#0093d0',
  NIPPARTS: '#0069b4',
  JAPANPARTS: '#c8102e',
  KYB: '#00519e',
  MONROE: '#e4002b',
  'K&N': '#e4002b',
  PURFLUX: '#0069b4',
  UFI: '#e30613',
  'HENGST FILTER': '#0069b4',
  BOSAL: '#005ca9',
  WALKER: '#e4002b',
  ATE: '#003da5',
  FERODO: '#00843d',
  TEXTAR: '#e4002b',
  JURID: '#0069b4',
  PAGID: '#e30613',
  'A.B.S.': '#0069b4',
  TOTAL: '#e4002b',
  CASTROL: '#00843d',
  MOBIL: '#0033a0',
  LIQUI_MOLY: '#e30613',
  'LIQUI MOLY': '#e30613',
  VARTA: '#0069b4',
  EXIDE: '#e4002b',
  BANNER: '#e30613',
};

/**
 * OKLCH tone of a brand accent: hue and chroma carry the identity, lightness is supplied per
 * theme by CSS.
 *
 * Lightness cannot travel with the colour. The badge paints text and a 1.5px border in one colour
 * on a surface that is white in light theme and #0f1622 in dark — MANN-FILTER's yellow is
 * invisible on white, HELLA's navy is invisible on the dark surface, and no single value satisfies
 * both. So a colour is split: hue + chroma inline, lightness from `.dark`.
 */
interface BrandTone {
  /** OKLCH hue, 0–360. */
  h: number;
  /** OKLCH chroma. */
  c: number;
  /** Lightness on a light surface — the brand's own, floored so it stays readable on white. */
  l: number;
  /** Lightness on the dark surface — raised until it carries against #0f1622. */
  ld: number;
}

/**
 * Legibility target: WCAG AA for normal text. The badge label is 11px, so 4.5:1 is the bar that
 * applies — the large-text 3:1 allowance does not.
 */
const MIN_CONTRAST = 4.6; // a hair over 4.5 so rounding never lands under
/** Neon is noise on a dense list; the cap costs no recognisability. */
const MAX_C = 0.19;

/** The two surfaces a badge sits on: `--mds-surface` in each theme. */
const LIGHT_SURFACE = 1; // #ffffff, relative luminance 1
const DARK_SURFACE = relLuminance(15, 22, 34); // #0f1622

function relLuminance(r: number, g: number, b: number): number {
  const f = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

/** OKLCH → sRGB relative luminance. Clamped to gamut, which is what a browser paints anyway. */
function oklchLuminance(l: number, c: number, h: number): number {
  const rad = (h * Math.PI) / 180;
  const a = c * Math.cos(rad);
  const bb = c * Math.sin(rad);
  const l_ = (l + 0.3963377774 * a + 0.2158037573 * bb) ** 3;
  const m_ = (l - 0.1055613458 * a - 0.0638541728 * bb) ** 3;
  const s_ = (l - 0.0894841775 * a - 1.291485548 * bb) ** 3;
  const enc = (v: number) => {
    const x = Math.min(1, Math.max(0, v));
    return (x <= 0.0031308 ? 12.92 * x : 1.055 * x ** (1 / 2.4) - 0.055) * 255;
  };
  return relLuminance(
    enc(4.0767416621 * l_ - 3.3077115913 * m_ + 0.2309699292 * s_),
    enc(-1.2684380046 * l_ + 2.6097574011 * m_ - 0.3413193965 * s_),
    enc(-0.0041960863 * l_ - 0.7034186147 * m_ + 1.707614701 * s_)
  );
}

const contrast = (a: number, b: number) => (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);

/**
 * The lightness at which this hue is readable on `surface` — the brand's OWN lightness whenever
 * that already passes, otherwise the nearest one that does.
 *
 * Solved rather than tuned by hand, because a fixed cap cannot be right for every hue: at equal
 * OKLCH lightness a yellow carries far more luminance than a blue, so the constant that keeps
 * MANN-FILTER's yellow readable on white would needlessly flatten HELLA's navy. Searching per
 * brand keeps each one as close to its real colour as legibility allows — and a brand added to
 * the table years from now is corrected automatically instead of silently rendering unreadable.
 */
function legibleL(l: number, c: number, h: number, surface: number, darken: boolean): number {
  if (contrast(oklchLuminance(l, c, h), surface) >= MIN_CONTRAST) return l;
  let lo = darken ? 0 : l;
  let hi = darken ? l : 1;
  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2;
    const ok = contrast(oklchLuminance(mid, c, h), surface) >= MIN_CONTRAST;
    // Darkening: everything below the crossing point passes, so keep the highest that does.
    if (ok === darken) lo = mid;
    else hi = mid;
  }
  return darken ? lo : hi;
}

/** sRGB hex → OKLCH. Exact conversion, so a curated hex stays the colour it names. */
function hexToOklch(hex: string): { l: number; c: number; h: number } {
  const n = parseInt(hex.replace('#', ''), 16);
  const toLinear = (v: number) => {
    const s = v / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const r = toLinear((n >> 16) & 255);
  const g = toLinear((n >> 8) & 255);
  const b = toLinear(n & 255);
  const l_ = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m_ = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s_ = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_;
  const A = 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_;
  const B = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_;
  const h = (Math.atan2(B, A) * 180) / Math.PI;
  return { l: L, c: Math.hypot(A, B), h: h < 0 ? h + 360 : h };
}

/**
 * A brand we hold no colour for still gets one of its own — hashed from the name, so it is stable
 * across sessions, pages and result sets. FNV-1a over the normalized name, then the golden angle,
 * which spreads consecutive hashes far apart instead of clustering them.
 */
function derivedHue(brand: string): number {
  let hash = 2166136261;
  for (let i = 0; i < brand.length; i++) {
    hash ^= brand.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return ((hash >>> 0) * 137.508) % 360;
}

export function brandTone(brand: string | null | undefined): BrandTone {
  const key = brand?.trim().toUpperCase();
  if (!key) return { h: 0, c: 0, l: 0.55, ld: 0.72 }; // no brand → neutral grey
  const hex = BRAND_COLORS[key];
  const { l, c, h } = hex ? hexToOklch(hex) : { l: 0.55, c: 0.12, h: derivedHue(key) };
  const chroma = Math.min(c, MAX_C);
  return {
    h,
    c: chroma,
    l: legibleL(l, chroma, h, LIGHT_SURFACE, true),
    ld: legibleL(l, chroma, h, DARK_SURFACE, false),
  };
}

/**
 * Inline custom properties for a brand badge. Pair with `className="badge brand"` — the stylesheet
 * turns these into a colour at the lightness the current theme needs.
 */
export function brandStyle(brand: string | null | undefined): React.CSSProperties {
  const t = brandTone(brand);
  return {
    '--brand-h': t.h.toFixed(1),
    '--brand-c': t.c.toFixed(4),
    '--brand-l': t.l.toFixed(4),
    '--brand-ld': t.ld.toFixed(4),
  } as React.CSSProperties;
}

/** Brand pill, identical look to the mockup's `.bbadge`. */
export function BrandBadge({ brand }: { brand: string | null | undefined }) {
  if (!brand) return <span className="bbadge muted">—</span>;
  return (
    <span className="bbadge brand" style={brandStyle(brand)}>
      {brand}
    </span>
  );
}

/** Generic auto-part placeholder thumbnail (radiator-style), as in the mockup. */
export function PartThumb({ size = 46 }: { size?: number }) {
  const h = Math.round((size * 38) / 46);
  return (
    <svg width={size} height={h} viewBox="0 0 46 38" aria-hidden>
      <rect x="6" y="6" width="34" height="26" rx="2" fill="#dfe3e8" stroke="#aeb4bd" />
      <rect x="9" y="9" width="28" height="20" rx="1" fill="#eef1f4" />
      {Array.from({ length: 13 }, (_, i) => (
        <line
          key={i}
          x1={10 + i * 2.1}
          y1="10"
          x2={10 + i * 2.1}
          y2="28"
          stroke="#9aa1ab"
          strokeWidth="0.9"
        />
      ))}
      <rect x="4" y="3" width="38" height="4" rx="1.5" fill="#b8bec6" />
      <rect x="4" y="31" width="38" height="4" rx="1.5" fill="#b8bec6" />
      <rect x="1" y="12" width="5" height="6" rx="1.5" fill="#8a929c" />
      <rect x="40" y="20" width="5" height="6" rx="1.5" fill="#8a929c" />
    </svg>
  );
}
